<template>
  <div
    role="tablist"
    class="lp-tabs"
    :class="{ 'lp-tabs-v': vertical, 'lp-tabs-boxed': box }"
  >
    <div class="lp-tabs-wrapper ly-row ly-spacebetween-center">
      <div
        class="lp-tabs-header"
        :class="{ 'lp-tabs-header-v': vertical }"
        :aria-orientation="vertical ? 'vertical' : 'horizontal'"
      >
        <div
          v-for="(tab, i) in tabs"
          :ref="
            (el) => {
              if (el) tabsRefs[i] = el
            }
          "
          :key="i"
          role="tab"
          :aria-selected="selectedIndex === i"
          :tabindex="selectedIndex === i ? null : -1"
          class="lp-tabs-tab py-2 px-1 font-weight-medium"
          :class="{
            'lp-tabs-tab-active text-blue': selectedIndex === i,
            'lp-tabs-tab-disabled': tab.disabled,
          }"
          :disabled="tab.disabled"
          :data-content="tab.title"
          @keydown.right.prevent="vertical ? null : focusNextTab(i + 1)"
          @keydown.left.prevent="vertical ? null : focusPrevTab(i - 1)"
          @keydown.down.prevent="vertical ? focusNextTab(i + 1) : null"
          @keydown.up.prevent="vertical ? focusPrevTab(i - 1) : null"
          @click="selectTab(i)"
        >
          {{ tab.title }}
        </div>
      </div>
      <div v-if="$slots.actions">
        <slot name="actions" />
      </div>
    </div>
    <slot />
  </div>
</template>

<script setup lang="ts">
import { nextTick, onMounted, provide, ref, watch } from 'vue'
import { Tab } from './types'

defineOptions({
  name: 'lp-tabs',
})

type Props = {
  activeTab?: number
  vertical?: boolean
  box?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  activeTab: 0,
  vertical: false,
  box: false,
})
const emit = defineEmits(['click', 'onActiveTabChange'])

const selectedIndex = ref(props.activeTab)
const tabs = ref<Tab[]>([])
const tabsRefs = ref<HTMLElement[]>([])

provide('tabs', tabs)

onMounted(async () => {
  await nextTick()
  selectTab(selectedIndex.value)
})

watch(
  () => props.activeTab,
  (newIndex, oldIndex) => {
    if (newIndex !== oldIndex) {
      selectedIndex.value = newIndex
      selectTab(newIndex)
    }
  },
)

function selectTab(i: number) {
  if (tabs.value?.[i]?.disabled) {
    return
  }
  selectedIndex.value = i
  tabs.value.forEach((tab, index) => {
    tab.isActive = index === i
    if (index === i && typeof tab.onClick === 'function') {
      tab.onClick()
    }
  })
  emit('click', true)
  emit('onActiveTabChange', selectedIndex.value)
}

function focusNextTab(i: number) {
  if (!tabs.value[i]) {
    focusNextTab(0)
    return
  }
  if (tabs.value?.[i]?.disabled) {
    focusNextTab(i + 1)
    return
  }
  tabsRefs.value[i].focus()
}

function focusPrevTab(i: number) {
  if (!tabs.value[i]) {
    focusPrevTab(tabsRefs.value.length - 1)
    return
  }
  if (tabs.value?.[i]?.disabled) {
    focusPrevTab(i - 1)
    return
  }
  tabsRefs.value[i].focus()
}
</script>
